import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Menu, X, ArrowUpRight, Globe, Check, ChevronDown } from "lucide-react";
import { useLanguage, LANGUAGES } from "./LanguageContext";
import { useDynamicConfig } from "./DynamicConfig";

export default function Navbar() {
  const { language, setLanguage, t, currentConfig } = useLanguage();
  const { logoText, logoChar } = useDynamicConfig();
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const [activeLink, setActiveLink] = useState<string>("");
  const langMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);

      // Highly precise scroll progress percentage for the master progress line
      const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (totalScroll > 0) {
        setScrollProgress(window.scrollY / totalScroll);
      } else {
        setScrollProgress(0);
      }
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target as Node)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    // Sync initially with URL hash (e.g., #products or #about-company)
    const syncHash = () => {
      setActiveLink(window.location.hash || "#");
    };
    window.addEventListener("hashchange", syncHash);
    syncHash();

    // Intersection Observer to highlight sections as you scroll on the home screen
    const sections = ["automation-services", "services", "products", "about-company", "contact"];
    const observerOptions = {
      root: null,
      rootMargin: "-35% 0px -45% 0px", // Optimizes highlight trigger when section is centered
      threshold: 0.1,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const id = entry.target.getAttribute("id");
          if (id) {
            setActiveLink(`#${id}`);
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => {
      window.removeEventListener("hashchange", syncHash);
      observer.disconnect();
    };
  }, []);

  const navLinks = [
    { name: t("aiAutomations", "navbar", "AI Automations"), href: "#automation-services" },
    { name: t("services", "navbar", "Services"), href: "#services" },
    { name: t("products", "navbar", "Products"), href: "#products" },
    { name: t("aboutCompany", "navbar", "About Us"), href: "#about-company" },
    { name: t("contact", "navbar", "Contact"), href: "#contact" },
  ];

  return (
    <>
      {/* High-fidelity kinetic scroll indicator bar */}
      <div
        className="fixed top-0 left-0 right-0 h-[3px] z-55 pointer-events-none transition-all duration-75 origin-left"
        style={{
          background: "linear-gradient(90deg, #4f46e5 0%, #6366f1 50%, #818cf8 100%)",
          transform: `scaleX(${scrollProgress})`
        }}
      />

      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-0 left-0 w-full z-50 pointer-events-none transition-all duration-305"
      >
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-6">
          <motion.div
            layout
            className={`pointer-events-auto flex items-center justify-between mx-auto transition-all duration-500 ease-out ${
              scrolled
                ? "max-w-5xl px-6 py-3.5 bg-white/85 backdrop-blur-md border border-gray-150/40 rounded-full shadow-[0_16px_40px_-12px_rgba(0,0,0,0.06)] ring-1 ring-black/2"
                : "w-full px-5 py-4 bg-transparent border border-transparent"
            }`}
          >
            {/* Kinetic Branding Monogram */}
            <a href="#" className="flex items-center gap-3.5 group focus:outline-hidden">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="relative w-9 h-9 flex items-center justify-center cursor-pointer"
              >
                {/* Rotating outer ring */}
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-0 border border-indigo-200 rounded-lg group-hover:border-indigo-500 transition-colors shadow-2xs"
                />
                {/* Rotating dashed middle ring */}
                <motion.div
                  animate={{ rotate: -360 }}
                  transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-1.5 border border-dashed border-indigo-400 rounded opacity-40 group-hover:opacity-100 transition-opacity"
                />
                {/* Inner background glow */}
                <span className="absolute inset-2 bg-indigo-50/50 rounded group-hover:bg-indigo-100/70 transition-all duration-300 scale-90 group-hover:scale-100 group-hover:shadow-[0_0_12px_rgba(99,102,241,0.2)]" />
                {/* Monogram character */}
                <span className="relative font-display font-black text-[#4f46e5] group-hover:text-black transition-colors z-10 select-none text-sm tracking-normal">
                  {logoChar}
                </span>
              </motion.div>
              <div className="flex flex-col text-left">
                <span className="font-display font-bold tracking-[0.22em] text-[#111827] text-xs leading-none transition-colors group-hover:text-indigo-600">
                  {logoText}
                </span>
                <span className="text-[7.5px] font-mono tracking-[0.25em] text-gray-400 uppercase mt-1.5 leading-none">
                  Private Limited
                </span>
              </div>
            </a>

            {/* Desktop Navigation Links - Changed breakpoint to lg:flex to avoid overlapping on tablets/iPads */}
            <nav aria-label="Desktop Navigation" className="hidden lg:flex items-center gap-1.5 bg-gray-50/60 p-1 rounded-full border border-gray-150/20 backdrop-blur-xs relative select-none">
              {navLinks.map((link) => {
                const isActive = activeLink === link.href;
                return (
                  <a
                    key={link.name}
                    href={link.href}
                    onMouseEnter={() => setHoveredLink(link.name)}
                    onMouseLeave={() => setHoveredLink(null)}
                    className={`relative px-4 py-2 text-[10.5px] tracking-widest font-mono transition-colors duration-300 rounded-full focus:outline-hidden flex flex-col items-center justify-center ${
                      isActive ? "text-indigo-600 font-semibold" : "text-gray-500 hover:text-[#111827]"
                    }`}
                  >
                    <span className="relative z-10">{link.name}</span>

                    {/* Sliding Hover background pill */}
                    <AnimatePresence>
                      {hoveredLink === link.name && (
                        <motion.span
                          layoutId="nav-hover-pill"
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          className="absolute inset-0 bg-white shadow-2xs border border-gray-150/40 rounded-full -z-0"
                          transition={{ type: "spring", stiffness: 380, damping: 28 }}
                        />
                      )}
                    </AnimatePresence>

                    {/* Active Link dot cursor indicating current section */}
                    {isActive && (
                      <motion.span
                        layoutId="nav-active-dot"
                        className="absolute -bottom-1 w-1 h-1 rounded-full bg-indigo-600"
                        transition={{ type: "spring", stiffness: 350, damping: 24 }}
                      />
                    )}
                  </a>
                );
              })}
            </nav>

            {/* Language Selection & Contact CTA */}
            <div className="hidden lg:flex items-center gap-4">
              {/* Language Switcher Dropdown */}
              <div className="relative" ref={langMenuRef}>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setLangMenuOpen(!langMenuOpen)}
                  className={`flex items-center gap-2 px-3.5 py-1.5 text-[10px] font-mono tracking-wider text-gray-500 hover:text-[#111827] border transition-all duration-300 rounded-full focus:outline-hidden cursor-pointer ${
                    langMenuOpen
                      ? "bg-white border-indigo-100 text-indigo-600 shadow-xs"
                      : "bg-transparent border-transparent hover:bg-white hover:border-gray-250/20 hover:shadow-2xs"
                  }`}
                >
                  <motion.span
                    animate={{ rotate: langMenuOpen ? 180 : 0 }}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <Globe size={11} className={langMenuOpen ? "text-[#4f46e5]" : "text-gray-400"} />
                  </motion.span>
                  <span className="flex items-center gap-1.5">
                    <span>{currentConfig.flag}</span>
                    <span className="uppercase font-semibold">{currentConfig.code}</span>
                  </span>
                  <ChevronDown size={11} className={`text-gray-400 transition-transform duration-300 ${langMenuOpen ? "rotate-180 text-indigo-400" : ""}`} />
                </motion.button>

                <AnimatePresence>
                  {langMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                      className="absolute right-0 mt-3 w-52 rounded-2xl border border-gray-150/45 bg-white/95 backdrop-blur-xl p-2.5 shadow-[0_20px_50px_rgba(0,0,0,0.08)] z-50 text-left"
                    >
                      <div className="text-[8.5px] font-mono tracking-[0.2em] text-gray-400 uppercase px-2.5 mb-1.5 select-none font-bold">
                        Select Language
                      </div>
                      <div className="grid grid-cols-1 gap-1">
                        {LANGUAGES.map((lang) => (
                          <button
                            key={lang.code}
                            onClick={() => {
                              setLanguage(lang.code);
                              setLangMenuOpen(false);
                            }}
                            className={`flex items-center justify-between w-full px-3 py-2 text-xs font-mono rounded-xl transition-all cursor-pointer text-left ${
                              language === lang.code
                                ? "bg-indigo-50/80 text-indigo-600 font-semibold border-l-2 border-indigo-600 pl-2.5"
                                : "text-gray-600 hover:bg-gray-50/80 hover:text-[#111827]"
                            }`}
                          >
                            <span className="flex items-center gap-2">
                              <span className="text-sm shrink-0">{lang.flag}</span>
                              <span className="tracking-wide">{lang.localName}</span>
                            </span>
                            {language === lang.code && (
                              <motion.span layoutId="active-lang-check">
                                <Check size={11} className="shrink-0 text-indigo-600" />
                              </motion.span>
                            )}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Magnetic Interactive CTA button */}
              <motion.a
                href="#contact"
                whileHover={{
                  scale: 1.03,
                  boxShadow: "0 10px 25px -5px rgba(99, 102, 241, 0.2)"
                }}
                whileTap={{ scale: 0.98 }}
                className="group text-[10px] tracking-widest font-mono font-semibold bg-[#111827] text-white hover:bg-[#4f46e5] px-5 py-2.5 rounded-full transition-all duration-300 flex items-center gap-2 focus:outline-hidden shadow-xs relative overflow-hidden"
              >
                {/* Horizontal shine reflection stream */}
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />

                <span className="relative z-10">{t("cta", "navbar")}</span>
                <ArrowUpRight size={12} className="relative z-10 shrink-0 transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300 pointer-events-none" />
              </motion.a>
            </div>

            {/* Mobile / Tablet Toggle Button */}
            <motion.button
              whileTap={{ scale: 0.92 }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle mobile menu"
              className={`lg:hidden p-2.5 rounded-full transition-all duration-300 focus:outline-hidden flex items-center justify-center border cursor-pointer ${
                mobileMenuOpen
                  ? "bg-indigo-100/70 border-indigo-200 text-indigo-600"
                  : "bg-white border-gray-150 text-gray-600 hover:border-gray-300 active:bg-gray-50"
              }`}
            >
              <motion.div
                animate={{ rotate: mobileMenuOpen ? 90 : 0 }}
                transition={{ duration: 0.3 }}
              >
                {mobileMenuOpen ? <X size={15} /> : <Menu size={15} />}
              </motion.div>
            </motion.button>
          </motion.div>
        </div>
      </motion.header>

      {/* Full-screen Cinematic Mobile Drawer Panel */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Split Sibling Backdrop for precise touch propagation */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="fixed inset-0 bg-gray-950/40 backdrop-blur-xs z-45 lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />

            {/* Drawer Sliding body */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 220 }}
              className="fixed right-0 top-0 bottom-0 h-full w-[85%] max-w-sm bg-white/95 backdrop-blur-2xl shadow-3xl flex flex-col p-6 z-50 text-left border-l border-gray-100 lg:hidden"
            >
              {/* Header inside Mobile Drawer */}
              <div className="flex items-center justify-between pb-6 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg border border-indigo-500 bg-indigo-50/50">
                    <span className="font-display font-black text-xs text-indigo-600 select-none">{logoChar}</span>
                  </div>
                  <span className="font-display font-bold tracking-[0.2em] text-[#111827] text-xs">
                    {logoText}
                  </span>
                </div>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label="Close mobile menu"
                  className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-full focus:outline-hidden transition-colors cursor-pointer"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Navigation & Language selection scroll container */}
              <div className="flex flex-col gap-6 mt-6 overflow-y-auto flex-grow flex-1 pr-1.5 scrollbar-thin">
                <div className="flex flex-col gap-3">
                  <span className="text-[9px] font-mono tracking-[0.25em] text-indigo-500 uppercase font-bold">
                    {t("navigation", "navbar")}
                  </span>
                  <nav aria-label="Mobile Navigation" className="flex flex-col gap-2.5 text-left">
                    {navLinks.map((link, idx) => {
                      const isActive = activeLink === link.href;
                      return (
                        <motion.a
                          key={link.name}
                          initial={{ x: 15, opacity: 0 }}
                          animate={{ x: 0, opacity: 1 }}
                          transition={{ delay: idx * 0.05, type: "spring", stiffness: 180, damping: 20 }}
                          href={link.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`text-lg font-display font-medium tracking-tight py-2 border-b border-gray-50 flex items-center justify-between group cursor-pointer ${
                            isActive ? "text-indigo-600 font-semibold border-b-indigo-200" : "text-gray-900 hover:text-indigo-600 transition-colors"
                          }`}
                        >
                          <span>{link.name}</span>
                          <ArrowUpRight size={13} className={`opacity-30 group-hover:opacity-100 transition-opacity text-indigo-500 ${isActive ? "opacity-100 text-indigo-600" : ""}`} />
                        </motion.a>
                      );
                    })}
                  </nav>
                </div>

                {/* Language selections in Mobile Drawer */}
                <div className="flex flex-col gap-3">
                  <span className="text-[9px] font-mono tracking-[0.25em] text-indigo-500 uppercase font-bold">
                    Select Language
                  </span>
                  <div className="grid grid-cols-2 gap-2 pb-1">
                    {LANGUAGES.map((lang, idx) => (
                      <motion.button
                        key={lang.code}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.12 + idx * 0.03, duration: 0.25 }}
                        onClick={() => {
                          setLanguage(lang.code);
                          setMobileMenuOpen(false);
                        }}
                        className={`flex items-center gap-2.5 p-2.5 rounded-xl border text-[11px] font-mono transition-colors text-left truncate cursor-pointer ${
                          language === lang.code
                            ? "bg-indigo-50 border-indigo-200 text-indigo-600 font-semibold"
                            : "bg-white border-gray-150 text-gray-650 hover:bg-gray-50 active:bg-gray-100/60"
                        }`}
                      >
                        <span className="text-sm shrink-0">{lang.flag}</span>
                        <span className="truncate">{lang.localName}</span>
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Footer Section in Mobile Drawer: Nested inside the scrolling stream to prevent clipping on small heights */}
                <div className="pt-6 border-t border-gray-100 mt-auto flex flex-col gap-3 pb-4 shrink-0">
                  <a
                    href="mailto:careers@teralumax.com"
                    className="text-xs font-mono text-gray-500 hover:text-indigo-600 transition-colors text-center py-3 px-4 bg-gray-50/50 hover:bg-indigo-50/30 rounded-xl border border-gray-150 flex items-center justify-center min-h-[44px]"
                  >
                    {t("emailUs", "navbar")}
                  </a>
                  <a
                    href="#contact"
                    onClick={() => setMobileMenuOpen(false)}
                    className="w-full text-center py-4 bg-[#4f46e5] text-white rounded-xl font-mono text-xs tracking-wider shadow-md hover:bg-gray-900 active:scale-[0.985] transition-all font-semibold uppercase flex items-center justify-center gap-1.5"
                  >
                    {t("ctaMobile", "navbar")} <ArrowUpRight size={13} />
                  </a>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
